import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { AppAccount } from '@frontend/App/Account/App.Account';
import { AddBot } from '@frontend/App/App.AddBot';
import { AppOnboardingBlockingOverlay } from '@frontend/App/App.OnboardingBlockingOverlay';
import { AppSuccessModal } from '@frontend/App/App.SuccessModal';
import { AppHamburgerButton } from '@frontend/App/AppHamburgerButton';
import { AppSidebar } from '@frontend/App/AppSidebar';
import { Topbar } from '@frontend/App/Topbar/App.Topbar';
import { RoleSelector } from '@frontend/components/RoleSelector';
import { SidebarInset, SidebarProvider } from '@frontend/components/ui/sidebar';
import { Logo } from '@frontend/shared/components/Logo';
import '@frontend/shared/css/base.css';
import '@frontend/shared/css/codeeditor.css';
import '@frontend/shared/css/editor.css';
import '@frontend/shared/css/focus-within-hack.css';
import '@frontend/shared/css/hover-within.css';
import { useIsEditor } from '@frontend/shared/hooks/shared.hook.useIsEditor';
import { useDirectoryTeam } from '@frontend/shared/hooks/useDirectory';

export interface LayoutProps {
  children: ReactNode;
}

export function App({ children }: LayoutProps) {
  const team = useDirectoryTeam();
  const isEditor = useIsEditor();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Control sidebar state based on screen size
  const [sidebarOpen, setSidebarOpen] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    // Set sidebar to be open by default on all screen sizes
    setSidebarOpen(true);
  }, []);

  // Role-based default landing pages
  useEffect(() => {
    if (pathname === '/') {
      // Redirect based on user role
      if (isEditor) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/questions', { replace: true });
      }
    }
  }, [pathname, isEditor, navigate]);

  // Redirect to onboarding if not complete
  useEffect(() => {
    if (pathname !== '/onboarding' && !team.onboarding_complete) {
      navigate('/onboarding');
    }
  }, [pathname, team.onboarding_complete, navigate]);

  // Redirect from onboarding if already complete
  useEffect(() => {
    if (pathname === '/onboarding' && team.onboarding_complete) {
      if (isEditor) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/questions', { replace: true });
      }
    }
  }, [pathname, team.onboarding_complete, isEditor, navigate]);

  if (pathname === '/onboarding') {
    // If onboarding is complete, don't render - let the redirect happen
    if (team.onboarding_complete) {
      return null;
    }
    return (
      <>
        {children}
        <AppAccount />
      </>
    );
  }

  // Require Slack bot installation before accessing the app
  if (team.onboarding_complete && !team.slack_bot_user_id) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Logo />
        <p className="mt-4">Add the bot to your Slack to use Question Base</p>
        <AddBot />
        <AppAccount />
        <AppOnboardingBlockingOverlay />
      </div>
    );
  }

  // For viewers, show simple layout without sidebar
  if (!isEditor) {
    return (
      <>
        <AppSuccessModal />
        <AppOnboardingBlockingOverlay />
        <div className="max-w-4xl mx-auto h-auto px-4 pb-24 w-full">
          <Topbar />
          {children}
        </div>
        <AppAccount />
        <RoleSelector />
      </>
    );
  }

  // For editors/admins, show layout with hamburger menu
  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
      <AppSidebar />
      <SidebarInset className="flex-1">
        <AppSuccessModal />
        <AppOnboardingBlockingOverlay />
        <div className="max-w-4xl mx-auto h-auto px-4 pb-24 w-full">
          <Topbar />
          {children}
        </div>
      </SidebarInset>
      {/* Desktop hamburger button in fixed position */}
      <div className="hidden lg:block">
        <AppHamburgerButton />
      </div>
      <RoleSelector />
    </SidebarProvider>
  );
}
